import {
	CircularProgress,
	Dialog,
	DialogTitle,
	TextField,
	Theme,
	makeStyles,
} from "@material-ui/core";
import { Button } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import { get } from "lodash-es";
import * as React from "react";
import { ChangeEvent, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { Action } from "../../models/Action";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Profile } from "../../models/Profile";
import {
	getActionsByProfileId,
	upsertProfile,
	upsertProfileActions,
} from "../../repositories/ProfilesRepository";
import { DataTable } from "./ProceduresDataTable";

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		"& .MuiFormLabel-asterisk": {
			display: "none",
		},
	},
}));

type Props = {
	profileForEdit: Profile | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const ActionsOfProfile = ({ profileForEdit, show, onHide }: Props) => {
	const classes = useStyles();
	const [actionsOfProfile, setActionsOfProfile] = useState<Action[] | null>(
		null,
	);

	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [actionsSelected, setActionsSelected] = useState<Action[]>([]);

	const [error, setError] = useState<string>("");

	const [profileName, setProfileName] = useState<string>("");
	const [nameError, setNameError] = useState<string>("");
	const [showEditInput, setShowEditInput] = useState<boolean>(false);

	const { handleSubmit } = useForm<Profile>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const handleChangeProfielName = (ev: any) => {
		setProfileName(ev.target.value);
	};

	const handleCancelEditProfileName = () => {
		setProfileName(profileForEdit?.ProfileName as string);
		setShowEditInput(false);
	};

	const onSubmit: SubmitHandler<Profile> = async () => {
		setIsSubmitLoading(true);

		const resp = await upsertProfileActions(profileForEdit!, actionsSelected);
		if (!resp.ok) setError(resp.message);
		if (profileForEdit?.ProfileName !== profileName) {
			const profileRes = await upsertProfile(
				profileForEdit?.ProfileId as number,
				profileName as string,
			);
			if (!profileRes.ok) setNameError(profileRes.message);
			else if (profileRes.ok && resp.ok) {
				setOpen(true);
				onHide(true);
			}
		} else {
			setOpen(true);
			onHide(true);
		}
		setIsSubmitLoading(false);
	};

	useEffect(() => {
		if (show) {
			setShowEditInput(false);
			(async () => {
				setProfileName(profileForEdit?.ProfileName as string);
				setIsLoading(true);
				setIsSubmitLoading(false);
				if (profileForEdit) {
					const resp = await getActionsByProfileId(profileForEdit);
					if (resp.ok)
						setActionsOfProfile(get(resp, "data.tables[0].rows", []));
					else setError(resp.message);

					setIsLoading(false);
				}
			})();
		}
	}, [show]);

	return (
		<>
			<MesfModal
				title="EDIT PROFILE"
				open={show}
				handleClose={() => onHide(false)}
				id="edit-profile-modal"
				maxWidth="lg"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "0 30px 15px" }}>
						<Grid
							container
							style={{ position: "relative" }}
							className={classes.root}
						>
							<Grid item md={12} xs={12}>
								<TextField
									id="outlined-basic"
									label="Name"
									variant="outlined"
									value={profileName}
									fullWidth
									size="small"
									margin="dense"
									autoComplete="off"
									onChange={(e: ChangeEvent) => handleChangeProfielName(e)}
									InputLabelProps={{
										shrink: true,
									}}
									required={true}
									error={profileName.length === 0}
								/>
							</Grid>
						</Grid>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "60px" }}
							>
								<CircularProgress size="3rem" />
							</Grid>
						)}
						{!error && !isLoading && actionsOfProfile && (
							<DataTable
								actionsOfProfile={actionsOfProfile}
								onSelectionChanged={setActionsSelected}
							/>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						{!isLoading && (
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => onHide(false)}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						)}
					</MesfModal.Actions>
				</form>
			</MesfModal>

			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="info" onClose={handleClose}>
					The actions of the profile were edited successfully
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Editing Profile"
			/>
			<ErrorModal
				error={nameError}
				onHide={() => setNameError("")}
				title="Error Editing Profile"
			/>
		</>
	);
};
